import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc } from 'firebase/firestore';
import { generateUnitInvoiceNumber } from '@/lib/invoice-utils';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { formatCurrency } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/swal';
import { getRsvpExpectedAmount } from '@/lib/event-utils';
import { RSVP, Event } from '@/types';
import { Users, PoundSterling } from 'lucide-react';

export default function GroupPaymentPage() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<Event | null>(null);
  const [myRsvps, setMyRsvps] = useState<RSVP[]>([]);
  const [selectedRsvps, setSelectedRsvps] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchData = async () => {
      if (!unitId || !eventId || !currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Fetch event
        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (eventDoc.exists()) {
          const data = eventDoc.data();
        const eventDate = data.date?.toDate() || new Date();
        setEvent({
            id: eventDoc.id,
            unitId,
            name: data.name,
          date: eventDate,
            location: data.location,
            costPerPerson: data.costPerPerson,
            questionnaire: data.questionnaire || [],
            maxCapacity: data.maxCapacity,
            currentAttendees: data.currentAttendees || 0,
            waitlistEnabled: data.waitlistEnabled || false,
            waitlist: data.waitlist || [],
            rsvpDeadline: data.rsvpDeadline?.toDate() || new Date(),
          paymentDeadline: data.paymentDeadline?.toDate() || eventDate,
            eventStatus: data.eventStatus,
            dresscode: data.dresscode,
            customInstructions: data.customInstructions,
          isPublic: data.isPublic ?? false,
          requirePrefixRankUnitInfo: data.requirePrefixRankUnitInfo ?? true,
            createdBy: data.createdBy,
            createdAt: data.createdAt?.toDate() || new Date(),
          });
        }

        // Fetch user's RSVPs and invited guests
        const rsvpsQuery = query(
          collection(db, 'units', unitId, 'events', eventId, 'rsvps'),
          where('userId', '==', currentUser.uid)
        );
        const rsvpsSnapshot = await getDocs(rsvpsQuery);
        
        const fetchedRsvps: RSVP[] = [];
        for (const rsvpDoc of rsvpsSnapshot.docs) {
          const data = rsvpDoc.data();
          if (data.deletedAt) {
            continue;
          }
          fetchedRsvps.push({
            id: rsvpDoc.id,
            eventId,
            userId: data.userId,
            name: data.name,
            rank: data.rank,
            unit: data.unit,
            email: data.email,
            responses: data.responses,
            status: data.status,
            invoiceId: data.invoiceId,
            paid: data.paid,
            invitedBy: data.invitedBy,
            checkedIn: data.checkedIn,
            checkInTime: data.checkInTime?.toDate(),
            specialRequests: data.specialRequests,
            plusOneAllowed: data.plusOneAllowed,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          });

          // Also fetch guests invited by this user
          const invitedQuery = query(
            collection(db, 'units', unitId, 'events', eventId, 'rsvps'),
            where('invitedBy', '==', rsvpDoc.id)
          );
          const invitedSnapshot = await getDocs(invitedQuery);
          
          for (const invitedDoc of invitedSnapshot.docs) {
            const invitedData = invitedDoc.data();
            if (invitedData.deletedAt) {
              continue;
            }
            fetchedRsvps.push({
              id: invitedDoc.id,
              eventId,
              userId: invitedData.userId,
              name: invitedData.name,
              rank: invitedData.rank,
              unit: invitedData.unit,
              email: invitedData.email,
              responses: invitedData.responses,
              status: invitedData.status,
              invoiceId: invitedData.invoiceId,
              paid: invitedData.paid,
              invitedBy: invitedData.invitedBy,
              checkedIn: invitedData.checkedIn,
              checkInTime: invitedData.checkInTime?.toDate(),
              specialRequests: invitedData.specialRequests,
              plusOneAllowed: invitedData.plusOneAllowed,
              createdAt: invitedData.createdAt?.toDate() || new Date(),
              updatedAt: invitedData.updatedAt?.toDate() || new Date(),
            });
          }
        }

        setMyRsvps(fetchedRsvps);
      } catch (error) {
        console.error('Error fetching data:', error);
        showError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [unitId, eventId, currentUser]);

  const toggleRsvp = (rsvpId: string) => {
    const newSelected = new Set(selectedRsvps);
    if (newSelected.has(rsvpId)) {
      newSelected.delete(rsvpId);
    } else {
      newSelected.add(rsvpId);
    }
    setSelectedRsvps(newSelected);
  };

  const selectAll = () => {
    setSelectedRsvps(new Set(myRsvps.map((r) => r.id)));
  };

  const deselectAll = () => {
    setSelectedRsvps(new Set());
  };

  const handleGroupPayment = async () => {
    if (selectedRsvps.size === 0) {
      showError('Please select at least one RSVP to pay for');
      return;
    }

    if (!event || !unitId || !eventId) {
      showError('Invalid event');
      return;
    }

    try {
      // Create a group invoice with all selected RSVPs
      const costPerPerson = event.costPerPerson || 0;
      const totalAmount = selectedRsvps.size * costPerPerson;
      const invoiceNumber = await generateUnitInvoiceNumber(db, unitId);

      const invoiceData = {
        eventId,
        unitId,
        ownerUid: currentUser!.uid,
        totalAmount,
        amountPaid: 0,
        outstandingBalance: totalAmount,
        status: 'outstanding',
        rsvps: Array.from(selectedRsvps),
        paymentHistory: [],
        paymentLink: '', // Will be generated after creation
        remindersSent: [],
        invoiceNumber,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const invoiceRef = await addDoc(
        collection(db, 'units', unitId, 'events', eventId, 'invoices'),
        invoiceData
      );

      // Update payment link
      const paymentLink = `${window.location.origin}/payment/${invoiceRef.id}`;
      await updateDoc(invoiceRef, { paymentLink });

      // Update all RSVPs with this invoice ID
      for (const rsvpId of selectedRsvps) {
        await updateDoc(
          doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId),
          { invoiceId: invoiceRef.id }
        );
      }

      showSuccess('Group invoice created! Redirecting to payment...');
      navigate(`/payment/${invoiceRef.id}`);
    } catch (error) {
      console.error('Error creating group invoice:', error);
      showError('Failed to create group invoice');
    }
  };

  const totalAmount = selectedRsvps.size * (event?.costPerPerson || 0);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Event Not Found</CardTitle>
              <CardDescription>The event you're looking for doesn't exist.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              ← Back to Dashboard
            </Button>
          </div>

          <h1 className="mb-8 text-3xl font-bold">Group Payment</h1>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Select RSVPs to Pay For</CardTitle>
                      <CardDescription>
                        Choose who you're paying for and we'll create a combined invoice
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={selectAll}>
                        Select All
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAll}>
                        Deselect All
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {myRsvps.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                      <Users className="mx-auto mb-4 h-12 w-12 opacity-20" />
                      <p>No RSVPs found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myRsvps.map((rsvp) => (
                        <div
                          key={rsvp.id}
                          className={`flex items-center justify-between rounded-lg border p-4 cursor-pointer transition-colors ${
                            selectedRsvps.has(rsvp.id)
                              ? 'border-primary bg-primary/5'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => toggleRsvp(rsvp.id)}
                        >
                          <div className="flex items-center space-x-4">
                            <input
                              type="checkbox"
                              checked={selectedRsvps.has(rsvp.id)}
                              onChange={() => toggleRsvp(rsvp.id)}
                              className="h-5 w-5 rounded border-gray-300"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div>
                              <div className="font-semibold">{rsvp.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {rsvp.rank} • {rsvp.responses?.Meal || 'No meal selected'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {(() => {
                              const zeroPaymentRequired = getRsvpExpectedAmount(rsvp, event) === 0;
                              const displayPaid = rsvp.paid || zeroPaymentRequired;
                              return displayPaid ? (
                                <Badge variant="success">Paid</Badge>
                              ) : (
                                <Badge variant="warning">Unpaid</Badge>
                              );
                            })()}
                            <span className="font-semibold">
                              {event.costPerPerson ? formatCurrency(event.costPerPerson) : 'N/A'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Payment Summary */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Payment Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Selected RSVPs</span>
                      <span className="font-semibold">{selectedRsvps.size}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Price per person</span>
                      <span className="font-semibold">{event.costPerPerson ? formatCurrency(event.costPerPerson) : 'N/A'}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-semibold">Total Amount</span>
                      <span className="text-2xl font-bold">{formatCurrency(totalAmount)}</span>
                    </div>
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    disabled={selectedRsvps.size === 0}
                    onClick={handleGroupPayment}
                  >
                    <PoundSterling className="mr-2 h-4 w-4" />
                    Pay for {selectedRsvps.size} {selectedRsvps.size === 1 ? 'Person' : 'People'}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    A single invoice will be created for all selected RSVPs
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

